import { useEffect } from "react";
import PropTypes from "prop-types";

import { useModal } from "../../hooks/useModal";
import { useStores } from "../../context/StoresContext";

import { InventarioAddNewStore } from "./InventarioAddNewStore";
import { InventarioSidebarItem } from "./InventarioSidebarItem";
import { InventarioSidebarSkeleton } from "./InventarioSidebarSkeleton";
import { useStoreOperations } from "../../hooks/inventario/useStoreOperations";
import { useProductOperations } from "../../hooks/inventario/useProductOperations";
import { TAB_NAMES } from "../../const/inventario";

/**
 * Muestra filtros por categoría/almacén según el tab activo.
 * Permite crear, activar/desactivar y editar un almacen (solo en tab Almacenes)
 *
 * @param {string} activeTab - Tab activo (Items, Almacenes, Periodicidad)
 * @param {string} selectedCategory - Categoría/almacén seleccionado
 * @param {Function} onSelectCategory - Callback al seleccionar categoría
 */
export const InventarioSidebar = ({ activeTab }) => {
  const { stores, selectedStoreData } = useStores();
  const {
    operations: storeOps,
    states: storeStates,
    computed: { stats: storeStats },
  } = useStoreOperations();
  const { operations: productOps, states: productStates } = useProductOperations();
  const { isOpen, open, close } = useModal();

  //* Carga inicial de productos
  useEffect(() => {
    const ensureProductsLoaded = async () => {
      if (activeTab !== TAB_NAMES.items && activeTab !== TAB_NAMES.stores) return;

      if (!selectedStoreData) return;
    };

    ensureProductsLoaded();
  }, [activeTab, selectedStoreData, productOps]);

  /**
   * Crea un nuevo almacén.
   * Solo disponible en tab "Almacenes"
   */
  const handleAddStore = async (values) => {
    try {
      const result = await storeOps.createAlmacen({
        name: values.storeName,
        active: values.status,
      });

      if (result.success) {
        console.log("Almacén creado exitosamente");
        close();
      } else {
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      console.error(error);
      return {
        success: false,
        error: error.message || "Error al crear almacén",
      };
    }
  };

  const storesCount = storeStats.total || 0;

  const headerTitle = storesCount > 0 ? `Almacenes (${storesCount})` : "Almacenes";

  if (storeStates.fetchState.loading) {
    return <InventarioSidebarSkeleton count={stores.length} activeTab={activeTab} />;
  }

  // ========== RENDER ==========
  return (
    <>
      <aside className="inv-sidebar" aria-label="Filtros de categoría de inventario">
        {/* Header solo para tab Almacenes */}
        {activeTab === TAB_NAMES.stores && (
          <div className="inv-sec-header">
            <span className="inv-sec-title">{headerTitle}</span>
            <button
              className="btn"
              onClick={open}
              disabled={storeStates.fetchState.loading || productStates.fetchState.loading}
            >
              Nuevo Almacén
            </button>
          </div>
        )}

        {/* Lista de items */}
        {stores.map((item) => (
          <InventarioSidebarItem
            key={`sidebar-item-${item.id}`}
            item={item}
            activeTab={activeTab}
          />
        ))}
      </aside>

      {/* Modal de nuevo almacén */}
      <InventarioAddNewStore
        visible={isOpen}
        onClose={close}
        onSubmit={handleAddStore}
        saving={storeStates.createState.loading}
      />
    </>
  );
};

InventarioSidebar.propTypes = {
  activeTab: PropTypes.string.isRequired,
};
